/**
 * itemProfitDataSource.ts — WatermelonDB → ItemProfitabilityInput bridge.
 *
 * Accrual basis: line items of the SALE and CREDIT_NOTE invoices raised in the
 * range (voided excluded), joined to their product's name and category. Mirrors
 * salesDataSource / pnlDataSource so the numbers reconcile across reports.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, LineItem, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Product, Category } from '@/db/models/Inventory';
import type { ItemProfitabilityInput, ItemProductInput } from './itemProfitability';

/** Load a store's item-level margin inputs for the half-open range `[from, to)`. */
export async function loadItemProfitability(
  storeId: string,
  from: Date,
  to: Date,
): Promise<ItemProfitabilityInput> {
  const fromMs = from.getTime();
  const toMs = to.getTime();

  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    Q.where('created_at', Q.gte(fromMs)),
    Q.where('created_at', Q.lt(toMs)),
  ).fetch();

  const typeByInvoiceId = new Map(invoices.map((i) => [i.id, i.type]));
  const invoiceIds = invoices.map((i) => i.id);

  const lineRows = await fetchInChunks(invoiceIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  // Products → name + category id.
  const productIds = [...new Set(lineRows.map((li) => li.productId))];
  const productRows = await fetchInChunks(productIds, (batch) =>
    database.get<Product>('products').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  // Category names for those products.
  const categoryIds = [
    ...new Set(productRows.map((p) => p.categoryId).filter((id): id is string => !!id)),
  ];
  const categoryRows = await fetchInChunks(categoryIds, (batch) =>
    database.get<Category>('categories').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const categoryName = new Map(categoryRows.map((c) => [c.id, c.name]));

  const products = new Map<string, ItemProductInput>(
    productRows.map((p) => [
      p.id,
      {
        name: p.name,
        category: (p.categoryId && categoryName.get(p.categoryId)) || 'Uncategorised',
      },
    ]),
  );

  return {
    lines: lineRows.map((li) => ({
      productId: li.productId,
      quantity: li.quantity,
      taxableAmount: li.taxableAmount,
      costPrice: li.costPrice,
      invoiceType: typeByInvoiceId.get(li.invoiceId) ?? 'SALE',
    })),
    products,
  };
}
